package kz.dentalcrm.app.screens.initial_inspection

import kotlinx.collections.immutable.PersistentList
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.launch
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.base.ViewEvent
import kz.dentalcrm.app.core.base.ViewSideEffect
import kz.dentalcrm.app.core.ui.InputValue
import kz.dentalcrm.app.core.utils.Result
import kz.dentalcrm.app.data.network.response_model.initial_inspection.InitialInspectionType
import kz.dentalcrm.app.data.network.response_model.initial_inspection.InspectionOption
import kz.dentalcrm.app.domain.InitialInspectionRepository
import org.koin.core.component.inject

data class InspectionOptionState(
    val option: InspectionOption,
    val isChecked: Boolean,
    val isLoading: Boolean = false
)

data class InspectionTypeState(
    val id: Int,
    val name: String,
    val options: List<InspectionOptionState>,
    val customInput: InputValue = InputValue()
)

data class InitialInspectionState(
    val isLoading: Boolean = false,
    val isSavingCustom: Boolean = false,
    val appointmentId: Int = 0,
    val inspectionTypes: PersistentList<InspectionTypeState> = persistentListOf()
)

sealed interface InitialInspectionEvent : ViewEvent {
    data class LoadData(val appointmentId: Int) : InitialInspectionEvent
    data class ToggleOption(
        val typeId: Int,
        val optionId: Int,
        val currentChecked: Boolean
    ) : InitialInspectionEvent
    data class CustomInputChanged(val typeId: Int, val value: String) : InitialInspectionEvent
    data object SaveCustomOptions : InitialInspectionEvent
    data object OnBack : InitialInspectionEvent
}

sealed interface InitialInspectionEffect : ViewSideEffect {
    data class ShowError(val message: String) : InitialInspectionEffect
    data object NavigateBack : InitialInspectionEffect
}

class InitialInspectionViewModel : BaseStateViewModel<InitialInspectionEvent, InitialInspectionState, InitialInspectionEffect>() {

    private val repository: InitialInspectionRepository by inject()

    override fun setInitialState(): InitialInspectionState = InitialInspectionState()

    override fun handleEvents(event: InitialInspectionEvent) {
        when (event) {
            is InitialInspectionEvent.LoadData -> loadData(event.appointmentId)
            is InitialInspectionEvent.ToggleOption -> toggleOption(
                event.typeId,
                event.optionId,
                event.currentChecked
            )
            is InitialInspectionEvent.CustomInputChanged -> customInputChanged(
                event.typeId,
                event.value
            )
            is InitialInspectionEvent.SaveCustomOptions -> saveCustomOptions()
            is InitialInspectionEvent.OnBack -> setEffect { InitialInspectionEffect.NavigateBack }
        }
    }

    private fun loadData(appointmentId: Int) {
        setState { copy(isLoading = true, appointmentId = appointmentId) }
        launch {
            when (val result = repository.getInitialInspections(appointmentId)) {
                is Result.Success -> {
                    val types = result.value.map { type ->
                        InspectionTypeState(
                            id = type.id,
                            name = type.name,
                            options = type.options.map { option ->
                                InspectionOptionState(
                                    option = option,
                                    isChecked = option.initialInspections.isNotEmpty()
                                )
                            },
                            customInput = InputValue(
                                value = type.customOptions.firstOrNull()?.value ?: ""
                            )
                        )
                    }.toPersistentList()
                    setState { copy(isLoading = false, inspectionTypes = types) }
                }
                is Result.Error -> {
                    setState { copy(isLoading = false) }
                    setEffect { InitialInspectionEffect.ShowError(result.message ?: "Error loading data") }
                }
                else -> {
                    setState { copy(isLoading = false) }
                }
            }
        }
    }

    private fun toggleOption(typeId: Int, optionId: Int, currentChecked: Boolean) {
        val newChecked = !currentChecked
        val appointmentId = viewState.value.appointmentId

        // Update UI immediately
        setState {
            copy(
                inspectionTypes = inspectionTypes.map { type ->
                    if (type.id == typeId) {
                        type.copy(
                            options = type.options.map { optionState ->
                                if (optionState.option.id == optionId) {
                                    optionState.copy(isChecked = newChecked, isLoading = true)
                                } else {
                                    optionState
                                }
                            }
                        )
                    } else {
                        type
                    }
                }.toPersistentList()
            )
        }

        // Make API call
        launch {
            when (val result = repository.toggleInspection(
                appointmentId = appointmentId,
                inspectionTypeId = typeId,
                inspectionOptionId = optionId,
                isChecked = newChecked
            )) {
                is Result.Success -> {
                    setState {
                        copy(
                            inspectionTypes = inspectionTypes.map { type ->
                                if (type.id == typeId) {
                                    type.copy(
                                        options = type.options.map { optionState ->
                                            if (optionState.option.id == optionId) {
                                                optionState.copy(isLoading = false)
                                            } else {
                                                optionState
                                            }
                                        }
                                    )
                                } else {
                                    type
                                }
                            }.toPersistentList()
                        )
                    }
                }
                is Result.Error -> {
                    // Revert UI on error
                    setState {
                        copy(
                            inspectionTypes = inspectionTypes.map { type ->
                                if (type.id == typeId) {
                                    type.copy(
                                        options = type.options.map { optionState ->
                                            if (optionState.option.id == optionId) {
                                                optionState.copy(isChecked = currentChecked, isLoading = false)
                                            } else {
                                                optionState
                                            }
                                        }
                                    )
                                } else {
                                    type
                                }
                            }.toPersistentList()
                        )
                    }
                    setEffect { InitialInspectionEffect.ShowError(result.message ?: "Error updating") }
                }
                else -> {
                    setState {
                        copy(
                            inspectionTypes = inspectionTypes.map { type ->
                                if (type.id == typeId) {
                                    type.copy(
                                        options = type.options.map { optionState ->
                                            if (optionState.option.id == optionId) {
                                                optionState.copy(isLoading = false)
                                            } else {
                                                optionState
                                            }
                                        }
                                    )
                                } else {
                                    type
                                }
                            }.toPersistentList()
                        )
                    }
                }
            }
        }
    }

    private fun customInputChanged(typeId: Int, value: String) {
        setState {
            copy(
                inspectionTypes = inspectionTypes.map { type ->
                    if (type.id == typeId) {
                        type.copy(customInput = InputValue(value = value))
                    } else {
                        type
                    }
                }.toPersistentList()
            )
        }

    }

    private fun saveCustomOptions() {
        val appointmentId = viewState.value.appointmentId
        val typesToSave = viewState.value.inspectionTypes.filter {
            it.customInput.value.isNotBlank()
        }

        if (typesToSave.isEmpty()) return

        setState { copy(isSavingCustom = true) }

        launch {
            var anyError = false
            typesToSave.forEach { typeState ->
                val result = repository.addCustomInspection(
                    appointmentId = appointmentId,
                    inspectionTypeId = typeState.id,
                    value = typeState.customInput.value
                )
                if (result is Result.Error) {
                    anyError = true
                }
            }

            setState { copy(isSavingCustom = false) }

            if (anyError) {
                setEffect { InitialInspectionEffect.ShowError("Ошибка при сохранении") }
            }
            // Reload data to refresh custom_options from server
            loadData(appointmentId)
        }
    }
}

